/**
 * TurnTimeline - Turn-by-turn record of a kept hand playing solitaire
 *
 * One block per revealed turn showing the cards that entered and left the hand:
 * what was drawn, the land drop, and what got cast. Only turns the user has
 * stepped through are shown, so the timeline never leaks a draw that has not
 * happened yet.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoWarning } from 'react-icons/io5';

import type { CastEvent, SimCard, TurnAction, TurnSimulation } from '@/features/decks/lib/handSim';

import CastVsStuckCurve from './CastVsStuckCurve';
import type { SimCardTone } from './SimCardThumb';
import SimCardThumb from './SimCardThumb';

// ============================================================================
// Types
// ============================================================================

export interface TurnTimelineProps {
  /** The hand the simulation started from */
  hand: SimCard[];
  simulation: TurnSimulation;
  /** How many turns the user has stepped through */
  revealed: number;
  onOpenDetails: (card: SimCard) => void;
}

interface TurnEvent {
  card: SimCard;
  tone: SimCardTone;
  caption: string;
}

// ============================================================================
// Event derivation
// ============================================================================

/**
 * Cards that moved this turn, deduplicated: a land drawn and played the same
 * turn appears once, captioned with both actions.
 */
function turnEvents(action: TurnAction): TurnEvent[] {
  const events: TurnEvent[] = [];
  const drewKey = action.drawn?.key ?? null;
  const drewPrefix = (key: string): string => (key === drewKey ? 'drew · ' : '');

  if (action.landPlayed) {
    events.push({ card: action.landPlayed, tone: 'land', caption: `${drewPrefix(action.landPlayed.key)}land` });
  }

  for (const event of action.cast) {
    // Name the face for adventures and split cards, "cast" for everything else.
    const label = event.mode.name === event.card.name ? 'cast' : event.mode.name;
    events.push({ card: event.card, tone: 'cast', caption: `${drewPrefix(event.card.key)}${label}` });
  }

  if (action.drawn && !events.some((event) => event.card.key === drewKey)) {
    events.unshift({ card: action.drawn, tone: 'drawn', caption: 'drew' });
  }

  return events;
}

// ============================================================================
// Component
// ============================================================================

export default function TurnTimeline({ hand, simulation, revealed, onOpenDetails }: TurnTimelineProps): ReactElement {
  const visible = simulation.actions.slice(0, revealed);

  const drawn = visible.flatMap((action) => (action.drawn ? [action.drawn] : []));
  const resolved = new Set(
    visible
      .flatMap((action) => [...(action.landPlayed ? [action.landPlayed] : []), ...action.cast.map((event) => event.card)])
      .map((card) => card.key),
  );
  const stuck = [...hand, ...drawn].filter((card) => !resolved.has(card.key));
  const cast: CastEvent[] = visible.flatMap((action) => action.cast);

  const complete = revealed >= simulation.actions.length;
  const stalledFrom = complete ? simulation.stalledFrom : null;

  return (
    <div className="space-y-2">
      <ol className="space-y-1.5" aria-live="polite">
        {visible.map((action) => {
          const events = turnEvents(action);

          return (
            <li key={action.turn} className={clsx('space-y-1 rounded px-2 py-1.5', action.stalled ? 'bg-amber-500/10' : 'bg-zinc-800/40')}>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className={clsx('font-medium tabular-nums', action.stalled ? 'text-amber-300' : 'text-ash')}>T{action.turn}</span>
                <span className="text-ash/70 tabular-nums">
                  {action.landsInPlay} land{action.landsInPlay === 1 ? '' : 's'}
                  {action.manaUnspent > 0 && action.cast.length > 0 && ` · ${action.manaUnspent} unspent`}
                </span>
              </div>

              {events.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {events.map((event) => (
                    <SimCardThumb
                      key={`${event.card.key}-${event.tone}`}
                      card={event.card}
                      tone={event.tone}
                      caption={event.caption}
                      onOpenDetails={onOpenDetails}
                    />
                  ))}
                </div>
              )}

              {action.stalled && (
                <p className="flex items-center gap-1 text-[11px] font-medium text-amber-200">
                  <IoWarning className="h-3 w-3 shrink-0" aria-hidden="true" />
                  Stalled — nothing to play
                </p>
              )}
            </li>
          );
        })}
      </ol>

      <TimelineSummary simulation={simulation} stalledFrom={stalledFrom} complete={complete} />

      {stuck.length > 0 && (
        <div className="space-y-1">
          <span className="text-ash text-[10px] tracking-wider uppercase">Still in hand ({stuck.length})</span>
          <div className="flex flex-wrap gap-1.5">
            {stuck.map((card) => (
              <SimCardThumb
                key={card.key}
                card={card}
                tone="stuck"
                caption={card.isLand ? 'land' : `${card.cmc} mana`}
                onOpenDetails={onOpenDetails}
              />
            ))}
          </div>
        </div>
      )}

      <CastVsStuckCurve cast={cast} stuck={stuck} />
    </div>
  );
}

// ============================================================================
// Local presentation helpers
// ============================================================================

function TimelineSummary({
  simulation,
  stalledFrom,
  complete,
}: {
  simulation: TurnSimulation;
  stalledFrom: number | null;
  complete: boolean;
}): ReactElement | null {
  const notes: { key: string; tone: string; text: string }[] = [];

  if (stalledFrom !== null) {
    notes.push({ key: 'stalled', tone: 'text-amber-300', text: `Stuck from T${stalledFrom} — no land drop and nothing castable` });
  }
  if (complete && simulation.firstPlayTurn === null) {
    notes.push({ key: 'no-play', tone: 'text-rose-400', text: 'This hand never cast a spell' });
  }
  if (simulation.decked) {
    notes.push({ key: 'decked', tone: 'text-rose-400', text: 'Decked — the library ran out before the turn limit' });
  }

  if (notes.length === 0) return null;

  return (
    <div className="space-y-0.5" role="status">
      {notes.map((note) => (
        <p key={note.key} className={clsx('text-[11px]', note.tone)}>
          {note.text}
        </p>
      ))}
    </div>
  );
}
